import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { type CellContext, type ColumnDef } from "@tanstack/react-table";
import {
  ReportVersionStatusBadge,
  StatusIndicator,
  Tooltip,
} from "@/shared/components";
import { formatLtDateTime } from "@/utils/dateUtils";
import {
  useReportStatuses,
  useTradeDirections,
} from "@/hooks/useClassifierQueries";
import type { UiReportVersion } from "@/generated/ui-api";

export type VersionTableData = UiReportVersion & {
  reportingPeriod: string;
  tradeDirection: string;
};

export function useVersionColumns(tradeDirection?: string) {
  const { t } = useTranslation();
  const { data: tradeDirections } = useTradeDirections();
  const { data: reportStatuses } = useReportStatuses();

  const getTradeDirectionLabel = useCallback(
    (key?: string) => {
      return tradeDirections?.find(d => d.key === key)?.label || key || "";
    },
    [tradeDirections]
  );

  const getReportStatusLabel = useCallback(
    (key?: string) => {
      return reportStatuses?.find(s => s.key === key)?.label || key || "";
    },
    [reportStatuses]
  );

  const versionColumns = useMemo<ColumnDef<VersionTableData>[]>(
    () =>
      [
        {
          accessorKey: "reportingPeriod",
          id: "reportingPeriod",
          header: t("summaryPage.table.reportingPeriod"),
          size: 80,
          cell: (info: CellContext<VersionTableData, unknown>) => {
            const val = info.getValue() as string;
            return (
              <div className="flex justify-center items-center">{val}</div>
            );
          },
        },
        {
          accessorKey: "tradeDirection",
          id: "tradeDirection",
          header: t("reportPage.table.tradeDirection"),
          size: 80,
          cell: (info: CellContext<VersionTableData, unknown>) => (
            <div className="flex justify-center items-center">
              {getTradeDirectionLabel(info.getValue() as string)}
            </div>
          ),
        },
        {
          accessorKey: "versionNumber",
          id: "versionNumber",
          header: t("reportPage.table.versionNumber"),
          size: 50,
          cell: (info: CellContext<VersionTableData, unknown>) =>
            info.getValue() as number,
        },
        {
          accessorKey: "status",
          id: "status",
          size: 80,
          header: t("reportPage.table.status"),
          cell: (info: CellContext<VersionTableData, unknown>) => {
            const status = info.getValue() as string;
            return status ? (
              <div className="flex justify-center items-center">
                <ReportVersionStatusBadge status={status}>
                  {getReportStatusLabel(status)}
                </ReportVersionStatusBadge>
              </div>
            ) : (
              ""
            );
          },
        },
        {
          accessorKey: "versionDate",
          id: "versionDate",
          size: 80,
          header: t("reportPage.table.versionDate"),
          cell: (info: CellContext<VersionTableData, unknown>) => (
            <div className="flex justify-center items-center">
              {formatLtDateTime(info.getValue() as string)}
            </div>
          ),
        },
        {
          accessorKey: "isActual",
          id: "isActual",
          header: t("reportPage.table.isActual"),
          size: 50,
          cell: (info: CellContext<VersionTableData, unknown>) =>
            info.getValue() ? (
              <div className="flex justify-center items-center font-bold text-gray-900">
                {t("reportPage.table.yes")}
              </div>
            ) : (
              ""
            ),
        },
        {
          accessorKey: "isError",
          id: "isError",
          header: t("reportPage.table.isError"),
          size: 50,
          cell: (info: CellContext<VersionTableData, unknown>) => (
            <div className="flex justify-center items-center">
              {(info.getValue() as boolean) && (
                <Tooltip
                  content={t("reportPage.table.isError")}
                  variant="error"
                >
                  <div className="flex items-center">
                    <StatusIndicator status="error" size="lg" />
                  </div>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          accessorKey: "isWarning",
          id: "isWarning",
          header: t("reportPage.table.isWarning"),
          size: 50,
          cell: (info: CellContext<VersionTableData, unknown>) => (
            <div className="flex justify-center items-center">
              {(info.getValue() as boolean) && (
                <Tooltip content={t("reportPage.table.isWarning")}>
                  <div className="flex items-center">
                    <StatusIndicator status="warning" size="lg" />
                  </div>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          accessorKey: "nonValidPartnerIndicator",
          id: "nonValidPartnerIndicator",
          header: t("reportVersionHeaderPage.nonValidPartnerIndicator"),
          size: 80,
          cell: (info: CellContext<VersionTableData, unknown>) => {
            const val = info.getValue();
            if (val === undefined || val === null) return "";
            return (
              <div className="flex justify-center items-center">
                {val ? t("common.yes") : t("common.no")}
              </div>
            );
          },
        },
      ].filter(col => {
        if (tradeDirection !== "EXPORT") {
          return col.id !== "nonValidPartnerIndicator";
        }
        return true;
      }),
    [t, getReportStatusLabel, getTradeDirectionLabel, tradeDirection]
  );

  return {
    versionColumns,
    getTradeDirectionLabel,
    getReportStatusLabel,
  };
}
